import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Loader2 } from 'lucide-react';
import { fetchArticle, fetchArticles } from '../api/services';

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([fetchArticle(slug), fetchArticles({ limit: 6 })])
      .then(([art, all]) => {
        if (!alive) return;
        setArticle(art);
        setRelated(all.filter((a) => a.slug !== slug).slice(0, 3));
      })
      .catch(() => { if (alive) setArticle(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-40 pb-20 flex items-center justify-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat artikel...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h1 className="text-2xl font-bold">Artikel tidak ditemukan</h1>
        <Link to="/artikel" className="text-japo-green mt-4 inline-block">Kembali ke daftar artikel</Link>
      </div>
    );
  }

  const content = article.content || 'Konten artikel akan segera hadir.';

  return (
    <>
      <div className="pt-32 pb-8 bg-gradient-to-b from-rose-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <Link to="/artikel" className="inline-flex items-center gap-2 text-japo-green font-semibold text-sm mb-6 hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Artikel
          </Link>
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 text-xs mb-4">
              <span className="bg-japo-green/10 text-japo-green font-bold px-3 py-1 rounded-full">{article.category}</span>
              <span className="flex items-center gap-1 text-gray-500"><Calendar className="w-3 h-3" />{article.date}</span>
              <span className="flex items-center gap-1 text-gray-500"><User className="w-3 h-3" />{article.author}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-6 tracking-tight">{article.title}</h1>
            <p className="text-lg text-gray-600 leading-relaxed">{article.excerpt}</p>
          </div>
        </div>
      </div>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap">{content}</p>
              <blockquote className="border-l-4 border-japo-yellow bg-yellow-50 rounded-r-xl p-6 my-8">
                <p className="text-gray-800 italic">
                  &ldquo;Dengan bahan baku premium, Anda tidak perlu khawatir soal kualitas. Fokus saja pada pertumbuhan bisnis dan pelayanan pelanggan.&rdquo;
                </p>
              </blockquote>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-japo-green flex items-center justify-center text-white font-bold">
                  {article.author[0]}
                </div>
                <div>
                  <div className="font-bold text-gray-900">{article.author}</div>
                  <div className="text-xs text-gray-500">Content Writer</div>
                </div>
              </div>
              <button className="inline-flex items-center gap-2 text-japo-green font-semibold text-sm hover:text-japo-green-dark">
                <Share2 className="w-4 h-4" /> Bagikan
              </button>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <h3 className="text-2xl font-extrabold text-gray-900 mb-8">Artikel Terkait</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link key={a.id} to={`/artikel/${a.slug}`} className="group bg-white rounded-2xl overflow-hidden card-shadow border border-gray-100">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="text-xs bg-japo-green/10 text-japo-green font-bold px-2.5 py-1 rounded-full inline-block mb-3">{a.category}</div>
                    <h4 className="font-bold text-gray-900 group-hover:text-japo-green transition-colors line-clamp-2">{a.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
